import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { loginSuccess, setLoading, setError } from '../store/slices/authSlice';
import { RootState } from '../store/store';
import { showCustomToast } from '../components/common/CustomToast';
import SEO from '../components/common/SEO';
import LoginIllustration from '../assets/login.png';
import './LoginPage.css';

const LoginPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error } = useSelector((state: RootState) => state.auth);
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const [validationErrors, setValidationErrors] = useState({
    email: '',
    password: ''
  });

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Real-time validation
    if (name === 'email') {
      if (value && !validateEmail(value)) {
        setValidationErrors(prev => ({ ...prev, email: 'Invalid email format' }));
      } else {
        setValidationErrors(prev => ({ ...prev, email: '' }));
      }
    }

    if (name === 'password') {
      if (value && value.length < 8) {
        setValidationErrors(prev => ({ ...prev, password: 'Password must be at least 8 characters' }));
      } else {
        setValidationErrors(prev => ({ ...prev, password: '' }));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(setError(null));

    // Validate before submit
    if (!validateEmail(formData.email)) {
      dispatch(setError('Please enter a valid email address'));
      return;
    }

    dispatch(setLoading(true));

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      dispatch(loginSuccess({
        user: data.user,
        token: data.token,
      }));

      showCustomToast.success('Successfully logged in! Welcome back.', 4000);

      navigate('/');

    } catch (err: any) {
      console.error('Login error:', err);
      const errorMessage = err.message || 'Login failed';
      dispatch(setError(errorMessage));
      showCustomToast.error(errorMessage, 4000);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const hasValidationErrors = validationErrors.email || validationErrors.password;
  const canSubmit = formData.email && formData.password && !hasValidationErrors && !isLoading;

  return (
    <>
      <SEO 
        title="Login - SolBlox | Flip. Spin. Win."
        description="Login to SolBlox - the #1 solana based coinflipping site. Secure and transparent gaming platform."
        url="https://solblox.com/login"
      />
      <div className="auth-page">
      <div className="auth-container">
        <div className="auth-illustration">
          <img src={LoginIllustration} alt="Login" />
        </div>
        <div className="auth-content">
          <div className="auth-header">
            <h1>Welcome Back</h1>
            <p>Login to continue playing</p>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <div className="input-wrapper">
              <svg className="input-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="Email"
                disabled={isLoading}
                className={validationErrors.email ? 'error' : ''}
              />
            </div>
            {validationErrors.email && <span className="validation-error">{validationErrors.email}</span>}
          </div>

          <div className="form-group">
            <div className="input-wrapper">
              <svg className="input-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                minLength={8}
                placeholder="Password"
                disabled={isLoading}
                className={validationErrors.password ? 'error' : ''}
              />
            </div>
            {validationErrors.password && <span className="validation-error">{validationErrors.password}</span>}
          </div>

          {error && <div className="error-message">{error}</div>}

          <button 
            type="submit" 
            className="place-bet-btn auth-btn" 
            disabled={!canSubmit}
          >
            {isLoading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <div className="auth-switch">
          <p>
            Don't have an account? <Link to="/register" className="switch-link">Sign Up</Link>
          </p>
        </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default LoginPage;
